// JWT Token Utilities for SSO
// Simple JWT implementation without external dependencies

import { User } from '../context/AuthContext';

// Secret key for JWT signing (should be stored securely in production)
const JWT_SECRET = 'your-secret-key-here-change-in-production';
const JWT_EXPIRES_IN = 24 * 60 * 60; // 24 hours in seconds

/**
 * Simple base64 URL encoding
 */
const base64UrlEncode = (str: string): string => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Simple HMAC-SHA256 implementation
 */
const hmacSha256 = async (message: string, secret: string): Promise<Uint8Array> => {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
  return new Uint8Array(signature);
};

/**
 * Generate JWT token for SSO
 */
export const generateSSOToken = async (user: User): Promise<string | null> => {
  try {
    const header = {
      alg: 'HS256',
      typ: 'JWT'
    };

    const payload = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      iat: Math.floor(Date.now() / 1000), // Issued at
      exp: Math.floor(Date.now() / 1000) + JWT_EXPIRES_IN, // Expires in 24 hours
      iss: 'systango-analysis', // Issuer
      aud: 'mcpclient' // Audience
    };

    const encodedHeader = base64UrlEncode(JSON.stringify(header));
    const encodedPayload = base64UrlEncode(JSON.stringify(payload));
    const message = `${encodedHeader}.${encodedPayload}`;

    const signature = await hmacSha256(message, JWT_SECRET);
    const encodedSignature = base64UrlEncode(String.fromCharCode(...signature));

    const token = `${message}.${encodedSignature}`;

    console.log('🔐 Generated SSO Token:', token);
    console.log('📊 Token Payload:', payload);

    return token;
  } catch (error) {
    console.error('❌ Error generating SSO token:', error);
    return null;
  }
};

/**
 * Validate JWT token
 */
export const validateSSOToken = async (token: string): Promise<any> => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    const [header, payload, signature] = parts;
    const message = `${header}.${payload}`;

    // Verify signature
    const expectedSignature = await hmacSha256(message, JWT_SECRET);
    const encodedExpectedSignature = base64UrlEncode(String.fromCharCode(...expectedSignature));

    if (signature !== encodedExpectedSignature) {
      throw new Error('Invalid signature');
    }

    // Decode payload
    const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    // Check expiration
    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedPayload.exp < currentTime) {
      throw new Error('Token expired');
    }

    console.log('✅ Token validated:', decodedPayload);
    return decodedPayload;
  } catch (error) {
    console.error('❌ Token validation failed:', error);
    return null;
  }
};

/**
 * Create MCP client URL with SSO token
 */
export const createMCPClientURL = (token: string): string => {
  const baseURL = 'https://mcpclient.envistudios.com/sign-in';
  const urlWithToken = `${baseURL}?sso_token=${encodeURIComponent(token)}`;

  console.log('🔗 MCP Client URL with SSO Token:', urlWithToken);
  return urlWithToken;
};

/**
 * Extract token from URL parameters
 */
export const extractTokenFromURL = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('sso_token');

  if (token) {
    console.log('🔍 Extracted SSO Token from URL:', token);
  }

  return token;
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    if (!decoded || !decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('❌ Error checking token expiration:', error);
    return true;
  }
};

/**
 * Refresh token if needed
 */
export const refreshTokenIfNeeded = async (user: User): Promise<string | null> => {
  const currentToken = localStorage.getItem('sso_token');

  if (!currentToken || isTokenExpired(currentToken)) {
    console.log('🔄 Token expired or missing, generating new token...');
    const newToken = await generateSSOToken(user);
    if (newToken) {
      localStorage.setItem('sso_token', newToken);
      return newToken;
    }
  }

  return currentToken;
};
